import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCartCount } from "../../redux/cart";
import { useNavigate } from "react-router-dom";
import styles from "../../scss/common/common.module.scss";
import AppButton from "../../common/theme/Button";
import ProductCartShoppingCard from "./productCartShoppingCard";
import ShoppingProductEmpty from "../../common/sections/ShoppingProductEmpty";
import { Decrypt, Encrypt } from "../../common/utils/Functions";
import { toast } from "react-toastify";
import {
  acceptCartLineChange,
  fetchCartStoreChanges,
  removeCartLineById,
} from "../../common/utils/cartStoreChanges";
import type { CartLineChange } from "../../common/utils/cartStoreChanges";
import noteStyles from "../../scss/common/cartItemChangeNote.module.scss";
import {
  buildCartDisplayEntries,
  ensureCartLineIds,
  getAttachedSinglesForBundle,
  getBundleDisplayLabel,
  getBundleLineSubtotal,
  getBundleRowChoices,
  getIncludedInBundleText,
  shouldCountAttachedSinglePrices,
  shouldCountBundleProductPrices,
} from "../../common/utils/cartLineHelpers";
import BundleSelectForItemModal from "../productDetail/BundleSelectForItemModal";
interface IProps {
  product_id_set: any;
  set_product_id_set: any;
}

const BundleCartShoppingCard = ({
  product_id_set,
  set_product_id_set,
}: IProps) => {
  const navigate = useNavigate();
  const storeData = useSelector((state: any) => state.storeDataSlice.data);

  // const [open, setOpen] = React.useState(false);
  const [reLoadCart, setReLoadCart] = useState(false);

  // const handleOpen = () => setOpen(true);
  // const handleClose = () => {
  //   setReLoadCart(!reLoadCart);
  //   setOpen(false);
  // };
  const [cart, setCart] = useState<any>();

  const dispatch = useDispatch();
  const [totalProductPrice, setTotalProductPrice] = useState(0);
  const [totalProductItem, setTotalProductItem] = useState(0);
  const [isContinueDisable, setIsContinueDisable] = useState(false);
  const [bundleAssignOpen, setBundleAssignOpen] = useState(false);
  const [bundleAssignChoices, setBundleAssignChoices] = useState<any[]>([]);
  const [pendingStandaloneIndexes, setPendingStandaloneIndexes] = useState<number[]>([]);
  const [currentStandaloneName, setCurrentStandaloneName] = useState("");
  const [storeChanges, setStoreChanges] = useState<Record<string, CartLineChange>>({});

  const emptyCart = () => {
    localStorage.removeItem("cart");
    setReLoadCart(!reLoadCart);
  };

  useEffect(() => {
    const cartDataInfoD = localStorage?.getItem("cart");
    const cartDataInfo = Decrypt(cartDataInfoD);
    const cartData = cartDataInfo ? JSON.parse(cartDataInfo) : [];
    ensureCartLineIds(cartData);
    if (cartDataInfo) {
      localStorage.setItem("cart", Encrypt(JSON.stringify(cartData)));
    }
    setCart(cartData);
    if (cartData) dispatch(setCartCount(cartData.length));
  }, [reLoadCart]);

  useEffect(() => {
    let cancelled = false;
    const storeId = storeData?.id;
    if (!cart?.length || !storeId) {
      setStoreChanges({});
      return;
    }
    fetchCartStoreChanges(storeId, cart)
      .then((lines) => {
        if (cancelled) return;
        const next: Record<string, CartLineChange> = {};
        lines.forEach((line) => {
          next[String(line.cart_line_id)] = line;
        });
        setStoreChanges(next);
      })
      .catch(() => {
        if (!cancelled) setStoreChanges({});
      });
    return () => {
      cancelled = true;
    };
  }, [cart, storeData?.id]);

  const hasPendingStoreChanges = Object.keys(storeChanges).length > 0;

  const acceptStoreChange = (cartLineId: string | number) => {
    const line = storeChanges[String(cartLineId)];
    if (!line) return;
    const nextCart = acceptCartLineChange(cartLineId, line);
    dispatch(setCartCount(nextCart.length));
    toast.success("Cart updated with the latest item details");
    setReLoadCart(!reLoadCart);
  };

  const removeChangedItem = (cartLineId: string | number) => {
    const nextCart = removeCartLineById(cartLineId);
    dispatch(setCartCount(nextCart.length));
    toast.success("Removed successfully");
    setReLoadCart(!reLoadCart);
  };

  const editOptionValue = (productId: number, optionId: number, selectedValue: any) => {
    const product = cart?.findIndex(
      (product: any) => product.id === productId
    );
    if (product >= 0) {
      const optionIndex = cart?.[product]?.store_options?.findIndex(
        (option: any) => option.id === optionId
      );
      if (optionIndex >= 0 && cart?.[product]?.store_options) {
        cart[product].store_options[optionIndex].selectedData = selectedValue;
      }
    }
  };
  useEffect(() => {
    if (cart) {
      let totalPrice = 0;
      let totalItem = 0;
      const idArray: number[] = [];

      cart.map((cartItem: any) => {
        idArray.push(cartItem.id);
        if (cartItem.isSingleProduct) {
          if (cartItem.parentBundleLineId == null || cartItem.parentBundleLineId === "") {
            totalPrice += cartItem.club_price * cartItem.quantity;
            totalItem += Number(cartItem.quantity);
          }
        } else {
          const payType = storeData?.payment_handle_type;
          if (
            shouldCountBundleProductPrices(payType) ||
            shouldCountAttachedSinglePrices(payType)
          ) {
            totalPrice += getBundleLineSubtotal(cart, cartItem, payType);
            if (shouldCountBundleProductPrices(payType)) {
              cartItem.bundle_products?.forEach((product: any) => {
                totalItem += Number(product.store_product_detail.quantity);
              });
            }
            if (shouldCountAttachedSinglePrices(payType)) {
              getAttachedSinglesForBundle(cart, cartItem.cartLineId).forEach(
                (row: any) => {
                  totalItem += Number(row.quantity || 0);
                }
              );
            }
          }
        }
      });
      set_product_id_set(new Set(idArray));
      setTotalProductItem(totalItem);
      setTotalProductPrice(totalPrice);
    }
  }, [cart, reLoadCart]);

  const cartDisplayEntries = useMemo(
    () => (cart?.length ? buildCartDisplayEntries(cart) : []),
    [cart]
  );

  const openAssignmentFlow = (cartRows: any[]) => {
    const bundleChoices = getBundleRowChoices(cartRows);
    const unassignedIndexes = cartRows
      .map((item: any, index: number) =>
        item?.isSingleProduct && item?.parentBundleLineId == null ? index : -1
      )
      .filter((index: number) => index >= 0);

    if (!bundleChoices.length || !unassignedIndexes.length) {
      navigate("/checkout-cart");
      return;
    }

    setBundleAssignChoices(bundleChoices);
    setPendingStandaloneIndexes(unassignedIndexes);
    setCurrentStandaloneName(cartRows[unassignedIndexes[0]]?.product_name || "item");
    setBundleAssignOpen(true);
  };

  const handleContinueToCheckout = () => {
    const cartDataInfoD = localStorage?.getItem("cart");
    const cartDataInfo = Decrypt(cartDataInfoD);
    const cartRows = cartDataInfo ? JSON.parse(cartDataInfo) : [];
    ensureCartLineIds(cartRows);

    const hasBundle = cartRows.some((item: any) => !item?.isSingleProduct);
    const hasUnassignedSingle = cartRows.some(
      (item: any) => item?.isSingleProduct && item?.parentBundleLineId == null
    );

    if (hasBundle && hasUnassignedSingle) {
      openAssignmentFlow(cartRows);
      return;
    }

    navigate("/checkout-cart");
  };

  const handleAssignStandaloneToBundle = (cartLineId: number) => {
    const cartDataInfoD = localStorage?.getItem("cart");
    const cartDataInfo = Decrypt(cartDataInfoD);
    const cartRows = cartDataInfo ? JSON.parse(cartDataInfo) : [];
    ensureCartLineIds(cartRows);

    if (!pendingStandaloneIndexes.length) {
      setBundleAssignOpen(false);
      navigate("/checkout-cart");
      return;
    }

    const currentIndex = pendingStandaloneIndexes[0];
    if (cartRows[currentIndex]) {
      cartRows[currentIndex] = {
        ...cartRows[currentIndex],
        parentBundleLineId: cartLineId,
      };
    }
    localStorage.setItem("cart", Encrypt(JSON.stringify(cartRows)));

    const remainingIndexes = pendingStandaloneIndexes.slice(1);
    setPendingStandaloneIndexes(remainingIndexes);
    setReLoadCart(!reLoadCart);

    if (remainingIndexes.length > 0) {
      setCurrentStandaloneName(
        cartRows[remainingIndexes[0]]?.product_name || "item"
      );
      return;
    }

    setBundleAssignOpen(false);
    setCurrentStandaloneName("");
    navigate("/checkout-cart");
  };

  return (
    <>
      <BundleSelectForItemModal
        open={bundleAssignOpen}
        choices={bundleAssignChoices}
        onChoose={handleAssignStandaloneToBundle}
        title="Assign item to a bundle"
        message={`Before checkout, assign "${currentStandaloneName}" to one of the bundles in your cart.`}
        confirmLabel="Save and continue"
      />
      {cart && cart.length > 0 ? (
        <div>
          <div className={styles.shoppingChat_checkoutBtn_mob_view}>
            <h2>Shopping Cart</h2>
            <AppButton
              onClick={emptyCart}
              disabled={cart && cart.length <= 0}
              style={{
                cursor: cart && cart.length <= 0 ? "default" : "pointer",
              }}
              className={styles.empty_mob_view}
            >
              Empty Cart
            </AppButton>
          </div>
          {hasPendingStoreChanges ? (
            <p className={noteStyles.checkoutHint}>
              Accept the changes or remove the updated items before checkout.
            </p>
          ) : null}
          <div className={styles.shoppingCart}>
            {cart && cart.length > 0 ? (
              <>
                {cart &&
                  cartDisplayEntries.map((entry) =>
                    entry.kind === "standalone" ? (
                      <ProductCartShoppingCard
                        cartIndex={entry.index}
                        cartItem={entry.item}
                        reLoadCart={reLoadCart}
                        setReLoadCart={setReLoadCart}
                        setIsContinueDisable={setIsContinueDisable}
                        itemIndex={entry.index}
                        key={`cart-row-${entry.index}`}
                        editOptionValue={editOptionValue}
                        includedInBundleText={getIncludedInBundleText(
                          cart,
                          entry.item.parentBundleLineId
                        )}
                        storeChange={storeChanges[String(entry.item?.cartLineId)] || null}
                        onAcceptChanges={() => acceptStoreChange(entry.item?.cartLineId)}
                        onRemoveChangedItem={() =>
                          removeChangedItem(entry.item?.cartLineId)
                        }
                      />
                    ) : (
                      <ProductCartShoppingCard
                        key={`cart-bundle-${entry.index}`}
                        cartIndex={entry.index}
                        cartItem={entry.item}
                        reLoadCart={reLoadCart}
                        setReLoadCart={setReLoadCart}
                        setIsContinueDisable={setIsContinueDisable}
                        itemIndex={entry.index}
                        editOptionValue={editOptionValue}
                        attachedDisplaySingles={entry.attachedSingles}
                        bundleDisplayLabel={getBundleDisplayLabel(cart, entry.item)}
                        storeChange={storeChanges[String(entry.item?.cartLineId)] || null}
                        onAcceptChanges={() => acceptStoreChange(entry.item?.cartLineId)}
                        onRemoveChangedItem={() =>
                          removeChangedItem(entry.item?.cartLineId)
                        }
                      />
                    )
                  )}
                <div className={styles.shoppingChat_subtotal}>
                  <p>
                    Subtotal ({totalProductItem} items) :{" "}
                    {storeData.is_price_hide !== 1 &&
                    storeData.payment_handle_type !== 2 ? (
                      <b>{`$${parseFloat(totalProductPrice.toString()).toFixed(
                        2
                      )}`}</b>
                    ) : (
                      <b>{`$0.00`}</b>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <p style={{ textAlign: "center", padding: "10px" }}>
                Your Cart is Empty
              </p>
            )}
          </div>

          <div className={styles.shoppingChat_checkoutBtn}>
            <AppButton
              onClick={emptyCart}
              disabled={cart && cart.length <= 0}
              style={{
                cursor: cart && cart.length <= 0 ? "default" : "pointer",
              }}
              className={styles.empty}
            >
              Empty Cart
            </AppButton>
            <div className={styles.rightWrap}>
              <AppButton
                disabled={cart && cart.length <= 0}
                className={`${styles.continue} ${styles.shoppingBtn}`}
                style={{
                  textTransform: "initial",
                }}
                onClick={() => {
                  navigate("/");
                }}
              >
                Continue to Shopping
              </AppButton>
              <AppButton
                disabled={
                  (cart && cart.length <= 0) ||
                  isContinueDisable ||
                  hasPendingStoreChanges
                }
                style={{
                  cursor: hasPendingStoreChanges
                    ? "not-allowed"
                    : cart && cart.length <= 0
                    ? "default"
                    : "pointer",
                  textTransform: "initial",
                  // text-transform: "initial !important",
                }}
                className={`${styles.continue} ${
                  hasPendingStoreChanges ? styles.cartChangeBlocked : ""
                }`}
                onClick={handleContinueToCheckout}
              >
                Continue to Checkout
              </AppButton>
            </div>
          </div>
        
          {/* <ShoppingCards
            title="Westside Spandex"
            price="$250 - $300"
            button="Add to Chat"
          /> */}
        </div>
      ) : (
        <ShoppingProductEmpty />
      )}
    </>
  );
};

export default BundleCartShoppingCard;
